import { OrderSearchConditionEntity } from 'src/entities/OrderSearchCondition.entity';

import { isValidRow, normalizeFilterRows } from './presets';
import { FilterRow } from './types';

/**
 * 新地図・カード配車のフィルタープリセットであることの目印。
 *
 * 保存先 (`not_allocated_order_search_conditions`) は旧画面 `/plans` と共用で、
 * 会社ごとに 1 つのリストにまとまる。列名として実在しないこのキーを入れておくことで、
 * どちらの画面が保存した条件かを読み手が判別できる。
 */
export const UNASSIGNED_FILTER_CONDITION_KEY = '__unassigned_filter_v1__';

/**
 * API (`search_condition.rb`) が受け付ける演算子は決め打ちの語彙しかなく、
 * presence 検証もあるため空にできない。実際の演算子は value の JSON 側に入るので、
 * ここは語彙を満たすためだけのダミー。
 */
const PLACEHOLDER_OPERATOR = '含む';

/**
 * 行を旧形式 1 条件へ詰める。
 *
 * Why not 演算子・値を旧形式のフィールドへ写す: 旧形式は
 * `{logicalOperator, key, operator, value}` の 4 つしか持てず、operator の語彙にも
 * `...と一致しない` `空である` が無い。数値範囲・複数選択・時刻・boolean も表現できない。
 * 写すと保存のたびに条件が欠落するので、行そのものを JSON にして value へ入れる。
 *
 * logicalOperator にだけは OR を出しておく。旧画面がこの条件を読んだときも
 * グループ構造（どこで OR が切れるか）だけは壊れずに見える。
 */
export function encodePresetRows(rows: FilterRow[]): OrderSearchConditionEntity[] {
  return rows.map((row) => ({
    // 旧形式に 'where' は無い。先頭行は 'and' に潰し、decode 側でグループ構造から復元する
    logicalOperator: row.combinator === 'or' ? 'or' : 'and',
    key: UNASSIGNED_FILTER_CONDITION_KEY,
    operator: PLACEHOLDER_OPERATOR,
    value: JSON.stringify(row),
  }));
}

function parseRow(condition: OrderSearchConditionEntity): FilterRow | null {
  if (condition.key !== UNASSIGNED_FILTER_CONDITION_KEY) return null;
  try {
    const parsed: unknown = JSON.parse(condition.value ?? '');
    return isValidRow(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * 保存済み条件から行を復元する。壊れた行は落とすが、単純な filter では
 * OR 境界がずれる（presets.ts の normalizeFilterRows の説明を参照）。
 * 先頭行の 'where' もここで導出し直す。
 */
export function decodePresetRows(conditions: OrderSearchConditionEntity[]): FilterRow[] {
  // normalizeFilterRows はグループ境界を `combinator` から読む。旧形式のフィールド名は
  // logicalOperator なので、その名前で被せてから渡す（値の意味は同じ and / or）
  const shims = conditions.map((condition) => ({ combinator: condition.logicalOperator, condition }));
  return normalizeFilterRows(shims, (shim) => parseRow(shim.condition));
}

/** 新地図が保存したプリセットか。条件ゼロ件は判断材料が無いので false。 */
export function isUnassignedFilterCondition(
  preset: { searchConditions: OrderSearchConditionEntity[] },
): boolean {
  const conditions = preset.searchConditions ?? [];
  return conditions.length > 0 && conditions.every((c) => c.key === UNASSIGNED_FILTER_CONDITION_KEY);
}

/**
 * 旧画面 (/plans) が保存したプリセットか。取り込み一覧の抽出条件。
 * 条件ゼロ件を除くのは、取り込むものが無いプリセットを一覧に並べても選べないため。
 */
export function isLegacyPreset(
  preset: { searchConditions: OrderSearchConditionEntity[] },
): boolean {
  const conditions = preset.searchConditions ?? [];
  return conditions.length > 0 && !isUnassignedFilterCondition(preset);
}
